// Industry-standard Roster Excel export.
//
// Deliverable format the user asked for:
//   * Columns: Mon Tue Wed Thu Fri Sat Sun each showing the specific date.
//   * Rows: Staff names, sorted by the operator's custom Rank Hierarchy.
//   * Cells: "0700-1500" shift times. Annual leave cells (A/L) highlighted yellow.
//   * Grouping: staff grouped by hierarchy section (Leadership, Line, Support)
//     with a professional banded background per group.
//   * Print-ready: auto-fit column widths, frozen top row and first column,
//     landscape page setup, fit-to-page for A4.
//
// Uses exceljs (FOSS, full cell styling support). SheetJS Community strips
// cell styles on write, which is why this module does not use it.

import ExcelJS from "exceljs";
import { stampedFilename } from "./csvExport";
import { addDays, isoDate } from "./labor";

// Colour palette. All hex, exceljs expects "FFxxxxxx" ARGB with the alpha
// byte fixed to FF. Palette chosen to print cleanly on B&W printers by
// keeping section bands light enough to not overpower the shift numbers.
const COLOR = {
  headerRow:      "FF1B1A17",  // header row background
  headerText:     "FFFAF3DF",  // header row text
  bandLeadership: "FFF5EAC7",  // pale gold
  bandLine:       "FFE7EEF6",  // pale blue
  bandSupport:    "FFF4EBE1",  // warm sand
  annualLeave:    "FFFFEB99",  // yellow highlight for A/L cells
  cellText:       "FF1B1A17",  // near-black
  weekendCell:    "FFF7F4EE",  // very faint tint for Sat/Sun columns
  totalRow:       "FFEDEAE1",  // subtle divider band
  border:         "FFD4C89A",  // soft champagne border
};

const SECTION_TINT = {
  Leadership: COLOR.bandLeadership,
  Line:       COLOR.bandLine,
  Support:    COLOR.bandSupport,
};

// ISO weekday label ("Mon".."Sun") from an ISO date string, using UTC
// so the label never drifts by timezone.
function weekdayShort(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
}
function dayMonthLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" });
}
function isWeekend(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = dt.getUTCDay();
  return wd === 0 || wd === 6;
}

// Format a shift's start/end into "0700-1500" style. If the shift's notes
// field indicates annual leave the cell is A/L and we highlight yellow.
function formatShiftCell(sh) {
  const note = (sh.notes || "").toLowerCase();
  const isAL = /\b(a\/?l|annual\s*leave|leave|holiday|vacation)\b/.test(note);
  if (isAL) return { text: "A/L", isAL: true };
  const s = (sh.start_time || "").replace(":", "");
  const e = (sh.end_time || "").replace(":", "");
  if (!s || !e) return { text: "", isAL: false };
  return { text: `${s}-${e}`, isAL: false };
}

// Rank a staff member by the operator's hierarchy list. Unknown roles
// sink to the bottom of the sheet so the ladder integrity survives even
// when a staff record has a title that isn't in the operator's ranks.
function buildRankIndex(rankEntries) {
  const map = new Map();
  rankEntries.forEach((e, i) => {
    map.set((e.title || "").trim().toLowerCase(), { order: i, section: e.section || "Line" });
  });
  return map;
}
function lookupRank(role, rankIndex) {
  return rankIndex.get((role || "").trim().toLowerCase()) || { order: 9999, section: "Support" };
}

// Sort staff by (section order defined by first occurrence of each section
// in the rank list, then by rank order within that section, then by name).
function sortStaffByHierarchy(staff, rankEntries) {
  const rankIndex = buildRankIndex(rankEntries);
  // Section order comes from the first appearance of each section in the
  // rank list so an operator who ordered "Leadership, Line, Support" keeps
  // Leadership on top and Support on bottom.
  const sectionOrder = new Map();
  rankEntries.forEach((e, i) => {
    const s = e.section || "Line";
    if (!sectionOrder.has(s)) sectionOrder.set(s, sectionOrder.size);
  });

  return staff
    .filter((s) => s.active !== false)
    .map((s) => {
      const r = lookupRank(s.role, rankIndex);
      return {
        ...s,
        _rank_order: r.order,
        _section: r.section,
        _section_order: sectionOrder.has(r.section) ? sectionOrder.get(r.section) : 999,
      };
    })
    .sort((a, b) => {
      if (a._section_order !== b._section_order) return a._section_order - b._section_order;
      if (a._rank_order !== b._rank_order) return a._rank_order - b._rank_order;
      return (a.name || "").localeCompare(b.name || "");
    });
}

// Build a 14 (or 7) day date column list starting at `startDate`.
function buildDateColumns(startIso, days) {
  const [y, m, d] = startIso.split("-").map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, d));
  return Array.from({ length: days }, (_, i) => {
    const dt = new Date(Date.UTC(y, m - 1, d + i));
    return {
      iso: isoDate(dt),
      weekday: weekdayShort(isoDate(dt)),
      dayLabel: dayMonthLabel(isoDate(dt)),
      isWeekend: isWeekend(isoDate(dt)),
    };
  });
}

// Look up a shift for (staff_id, iso date). Returns the first shift found
// (kitchen rosters rarely have >1 shift per person per day but the
// exceljs cell can only render one so we take the earliest start).
function pickShift(shifts, staffId, iso) {
  const matches = shifts.filter((sh) => sh.staff_id === staffId && sh.date === iso);
  if (matches.length === 0) return null;
  matches.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  return matches[0];
}

/**
 * Build and download an industry-standard roster Excel workbook.
 *
 * @param {object} args
 * @param {Array} args.shifts   the shifts inside the current view range
 * @param {Array} args.staff    every staff record
 * @param {Array} args.rankEntries  the operator's hierarchy list (from /rank-hierarchy)
 * @param {string} args.rangeStart  ISO YYYY-MM-DD of the first date in the export
 * @param {number} args.days    7 for week, 14 for fortnight, 28+ for month
 * @param {string} args.restaurantName optional header caption
 */
export async function exportRosterIndustryXlsx({
  shifts, staff, rankEntries, rangeStart, days = 14, restaurantName = "",
}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Culinaire Specialist";
  wb.created = new Date();
  const ws = wb.addWorksheet("Roster", {
    views: [{ state: "frozen", xSplit: 2, ySplit: 3, activeCell: "C4" }],
    pageSetup: {
      orientation: "landscape",
      paperSize: 9,             // A4
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
  });

  const dateCols = buildDateColumns(rangeStart, days);
  const sortedStaff = sortStaffByHierarchy(staff, rankEntries);

  // ---------------- Title band ----------------
  const totalCols = 2 + dateCols.length; // Name, Role, then dates
  ws.mergeCells(1, 1, 1, totalCols);
  const titleCell = ws.getCell(1, 1);
  const rangeEnd = dateCols[dateCols.length - 1].iso;
  titleCell.value = `Roster: ${rangeStart} to ${rangeEnd}${restaurantName ? " (" + restaurantName + ")" : ""}`;
  titleCell.font = { name: "Calibri", size: 14, bold: true, color: { argb: COLOR.headerText } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR.headerRow } };
  ws.getRow(1).height = 26;

  // ---------------- Header rows ----------------
  const weekdayRow = ws.getRow(2);
  const dateRow = ws.getRow(3);
  weekdayRow.getCell(1).value = "Staff";
  weekdayRow.getCell(2).value = "Role";
  dateRow.getCell(1).value = "";
  dateRow.getCell(2).value = "";
  [1, 2].forEach((col) => {
    const c = weekdayRow.getCell(col);
    c.font = { bold: true, color: { argb: COLOR.headerText } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR.headerRow } };
    c.alignment = { vertical: "middle", horizontal: "left" };
    ws.mergeCells(2, col, 3, col);
  });
  dateCols.forEach((d, i) => {
    const wc = weekdayRow.getCell(3 + i);
    const dc = dateRow.getCell(3 + i);
    wc.value = d.weekday;
    dc.value = d.dayLabel;
    [wc, dc].forEach((c) => {
      c.font = { bold: true, color: { argb: COLOR.headerText }, size: 10 };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLOR.headerRow } };
      c.alignment = { vertical: "middle", horizontal: "center" };
      c.border = {
        top: { style: "thin", color: { argb: COLOR.border } },
        bottom: { style: "thin", color: { argb: COLOR.border } },
        left: { style: "thin", color: { argb: COLOR.border } },
        right: { style: "thin", color: { argb: COLOR.border } },
      };
    });
  });
  weekdayRow.height = 20;
  dateRow.height = 18;

  // ---------------- Body rows: grouped by section ----------------
  let rowIdx = 4;
  let currentSection = null;
  for (const s of sortedStaff) {
    // Insert a section band separator when section changes.
    if (s._section !== currentSection) {
      currentSection = s._section;
      const bandRow = ws.getRow(rowIdx);
      ws.mergeCells(rowIdx, 1, rowIdx, totalCols);
      const bc = bandRow.getCell(1);
      bc.value = currentSection;
      bc.font = { bold: true, size: 11, color: { argb: COLOR.cellText } };
      bc.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
      bc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SECTION_TINT[currentSection] || COLOR.totalRow } };
      bandRow.height = 20;
      rowIdx += 1;
    }

    const row = ws.getRow(rowIdx);
    row.getCell(1).value = s.name || "";
    row.getCell(2).value = s.role || "";
    [1, 2].forEach((col) => {
      const c = row.getCell(col);
      c.font = { bold: col === 1, size: 10, color: { argb: COLOR.cellText } };
      c.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SECTION_TINT[currentSection] || "FFFFFFFF" } };
      c.border = {
        bottom: { style: "hair", color: { argb: COLOR.border } },
        right: { style: "hair", color: { argb: COLOR.border } },
      };
    });

    dateCols.forEach((d, i) => {
      const cell = row.getCell(3 + i);
      const sh = pickShift(shifts, s.id, d.iso);
      if (sh) {
        const { text, isAL } = formatShiftCell(sh);
        cell.value = text;
        cell.font = { size: 10, bold: isAL, color: { argb: COLOR.cellText } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: isAL ? COLOR.annualLeave
              : (d.isWeekend ? COLOR.weekendCell : "FFFFFFFF"),
          },
        };
      } else {
        cell.value = "";
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: d.isWeekend ? COLOR.weekendCell : "FFFFFFFF" },
        };
      }
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        bottom: { style: "hair", color: { argb: COLOR.border } },
        right: { style: "hair", color: { argb: COLOR.border } },
      };
    });
    row.height = 22;
    rowIdx += 1;
  }

  // ---------------- Column widths ----------------
  // Auto-fit: Name column sized to the longest staff name (min 20, max 32).
  const maxNameLen = Math.max(
    5,
    ...sortedStaff.map((s) => (s.name || "").length),
    ...sortedStaff.map((s) => (s.role || "").length),
  );
  ws.getColumn(1).width = Math.min(32, Math.max(20, maxNameLen + 2));
  ws.getColumn(2).width = Math.min(28, Math.max(14, maxNameLen + 2));
  dateCols.forEach((_, i) => {
    ws.getColumn(3 + i).width = 12;
  });

  // Print options that make this file drop-on-a-clipboard ready.
  ws.pageSetup.printTitlesRow = "1:3";
  ws.pageSetup.horizontalCentered = true;

  // ---------------- Download ----------------
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const suffix = days === 14 ? "fortnight" : (days === 7 ? "week" : `${days}day`);
  a.download = stampedFilename(`roster_${suffix}_${rangeStart}_to_${rangeEnd}`, "xlsx");
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
